import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Message } from '../../models/message.model';
import { Contact } from '../../models/conversation.model';
import { ConversationService } from '../../services/conversation.service';
import { AuthService, UserRole } from '../../auth/auth.service';
import { TemplateQuickAccessComponent } from '../template-quick-access/template-quick-access.component';
import { CommunicationTemplate } from '../../services/communication-template.service';
import { RCOversightService } from '../../services/rc-oversight.service';
import { HttpClient } from '@angular/common/http';
import { config } from '../../config';

@Component({
  selector: 'app-messages',
  standalone: true,
  imports: [CommonModule, FormsModule, TemplateQuickAccessComponent],
  template: `
    <div class="messages-container">
      <div class="messages-list" #messagesList (scroll)="onMessagesScroll()">
        <div *ngIf="loading" class="loading-indicator">
          <span class="ap-spinner"></span>
          Loading messages...
        </div>

        <div *ngIf="false === loading && 0 === messages.length" class="empty-state">
          No messages yet. Start the conversation!
        </div>

        <ng-container *ngFor="let message of messages; let i = index">
          <!-- New messages divider: shown before the first new (unread) message -->
          <div
            *ngIf="i === newMessagesStartIndex && newMessagesStartIndex >= 0 && newMessagesStartIndex < messages.length"
            class="new-messages-divider"
            #newMessagesDivider>
            <div class="divider-line"></div>
            <span class="divider-badge">NEW</span>
          </div>

          <!-- Subject change divider -->
          <div *ngIf="shouldShowSubjectDivider(i)" class="subject-change-divider">
            <div class="subject-divider-line"></div>
            <span class="subject-divider-text">Subject: {{ messages[i].emailSubject }}</span>
            <div class="subject-divider-line"></div>
          </div>

          <div
            class="message-item"
            [ngClass]="{'message-sent': isSentByUser(message), 'message-received': !isSentByUser(message)}">
            <div class="message-content">
              <div *ngIf="isEmailChannel && message.emailSubject && !isNormalizedSubjectMatch(message.emailSubject, emailSubject)"
                   class="per-message-subject">
                {{ message.emailSubject }}
              </div>
              <div class="message-text">{{ message.text }}</div>
              <div class="message-info">
                <span class="message-sender">{{ getSenderName(message) }}</span>
                <span *ngIf="isEmailChannel && !isSentByUser(message)" class="via-email-badge">via Email</span>
                <span class="message-time">{{ message.datetime | date:'short' }}</span>
                <span *ngIf="isSentByUser(message)" class="message-status">
                  <span *ngIf="message.status === 'sending'" title="Sending">✓</span>
                  <span *ngIf="message.status === 'Sent'" title="Sent">✓✓</span>
                  <span *ngIf="message.status === 'Delivered'" title="Delivered">✓✓</span>
                  <span *ngIf="message.status === 'Failed'" title="Failed" class="status-failed">!</span>
                </span>
              </div>
            </div>
          </div>
        </ng-container>
      </div>
      
      <!-- Regular (non-email) input -->
      <div class="message-input-container" *ngIf="!isReadOnlyConversation() && !isEmailChannel">
        <div class="input-wrapper">
          <textarea
            [(ngModel)]="newMessage"
            placeholder="Type your message..."
            class="message-input"
            (keydown.enter)="handleSendMessage($event)"
            #messageTextarea></textarea>
          <button
            class="template-button"
            (click)="openTemplateModal()"
            title="Insert Template"
            type="button">
            📄
          </button>
        </div>
        <button
          (click)="handleSendMessage()"
          class="send-button"
          [disabled]="!newMessage.trim()">
          Send
        </button>
      </div>

      <!-- Email compose card -->
      <div class="email-compose" *ngIf="!isReadOnlyConversation() && isEmailChannel">
        <div class="email-compose-field email-compose-subject">
          <label class="email-compose-label">Subject</label>
          <input
            type="text"
            [(ngModel)]="emailSubjectInput"
            [placeholder]="emailSubject ? 'Re: ' + emailSubject : 'Enter subject...'"
            class="email-compose-subject-input" />
        </div>
        <div *ngIf="emailSubject" class="email-compose-subject-hint">
          Keep <strong>Re:</strong> to reply in the same thread — clear it and type a new subject to send a fresh email.
        </div>
        <div class="email-compose-field email-compose-body">
          <label class="email-compose-label">Body</label>
          <textarea
            [(ngModel)]="newMessage"
            placeholder="Write your email..."
            class="email-compose-textarea"
            (keydown.enter)="handleSendMessage($event)"
            #messageTextarea></textarea>
        </div>
        <div class="email-compose-footer">
          <button
            class="email-compose-template-btn"
            (click)="openTemplateModal()"
            title="Insert Template"
            type="button">
            📄 Template
          </button>
          <button
            (click)="handleSendMessage()"
            class="email-compose-send-btn"
            [disabled]="!newMessage.trim()">
            Send Email
          </button>
        </div>
      </div>

      <!-- Read-only message for MC conversations -->
      <div class="read-only-message" *ngIf="isReadOnlyConversation()">
        <div class="read-only-info">
          <span class="read-only-text">This is a supervised MC conversation. You can view messages but cannot send replies.</span>
        </div>
      </div>
    </div>
    
    <!-- Template Modal -->
    <div class="template-modal-overlay" *ngIf="showTemplateModal" (click)="closeTemplateModal()">
      <div class="template-modal-wrapper" (click)="$event.stopPropagation()">
        <app-template-quick-access
          [channelType]="channelType"
          (templateSelected)="onTemplateSelected($event)"
          (cancel)="closeTemplateModal()">
        </app-template-quick-access>
      </div>
    </div>
  `,
  styles: [`
    .messages-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 10px 15px;
    }

    .messages-list {
      flex: 1;
      overflow-y: auto;
      padding-right: 10px;
      margin-bottom: 8px;
    }
    
    .message-item {
      margin-bottom: 15px;
      display: flex;
    }
    
    .message-sent {
      justify-content: flex-end;
    }
    
    .message-received {
      justify-content: flex-start;
    }
    
    .message-content {
      max-width: 70%;
      padding: 10px;
      border-radius: 12px;
    }
    
    .message-sent .message-content {
      background-color: var(--ap-primary-soft, #E8EEFC);
      border-bottom-right-radius: 2px;
    }

    .message-received .message-content {
      background-color: var(--ap-surface-muted, #EEF0F3);
      border-bottom-left-radius: 2px;
    }
    
    .message-text {
      margin-bottom: 5px;
      word-wrap: break-word;
      white-space: pre-wrap;
      font-size: 14px;
    }

    @media (max-width: 576px) {
      .message-text {
        font-size: 13px;
      }
    }

    @media (min-width: 577px) and (max-width: 768px) {
      .message-text {
        font-size: 13.5px;
      }
    }

    @media (min-width: 769px) and (max-width: 1024px) {
      .message-text {
        font-size: 14px;
      }
    }

    @media (min-width: 1025px) {
      .message-text {
        font-size: 14px;
      }
    }
    
    .message-info {
      display: flex;
      justify-content: space-between;
      font-size: 0.8em;
      color: var(--ap-text-muted, #6B7280);
    }

    .message-status {
      margin-left: 5px;
      font-size: 0.9em;
    }

    .status-read {
      color: var(--ap-primary, #2F5BDA);
    }

    .status-failed {
      color: var(--ap-danger, #B42318);
      font-weight: bold;
    }

    .message-input-container {
      display: flex;
      margin-top: auto;
      border-top: 1px solid var(--ap-border, #E3E6EB);
      padding-top: 15px;
      align-items: flex-end;
      gap: 8px;
    }
    
    .input-wrapper {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
    
    }
    
    .template-button {
      position: absolute;
      right: 12px;
      top: 30%;
      transform: translateY(-50%);
      background: transparent;
      border: none;
      cursor: pointer;
      font-size: 18px;
      padding: 4px;
      color: var(--ap-text-muted, #6B7280);
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: var(--ap-radius-sm, 6px);
    }

    .template-button:hover {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-primary, #2F5BDA);
    }

    .message-input {
      flex: 1;
      min-height: 40px;
      max-height: 100px;
      padding: 10px 50px 10px 15px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: 20px;
      resize: none;
      font-family: inherit;
      outline: none;
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
    }

    .message-input:focus {
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: var(--ap-ring, 0 0 0 3px rgba(47,91,218,.18));
    }

    .send-button {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      border-radius: 20px;
      padding: 0 20px;
      cursor: pointer;
      font-weight: bold;
      height: 40px;
      flex-shrink: 0;
    }
    .send-button:hover:not(:disabled) { background: var(--ap-primary-ink, #1E3FA8); }

    .send-button:disabled {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-muted, #6B7280);
      cursor: not-allowed;
    }

    .loading-indicator, .empty-state {
      text-align: center;
      padding: 20px;
      color: var(--ap-text-muted, #6B7280);
    }

    .template-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(13,18,30,.45);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .template-modal-wrapper {
      width: 90vw;
      max-width: 700px;
    }

    /* Read-only aonversation styles */
    .read-only-message {
      padding: 15px;
      background: var(--ap-warn-soft, #FEF3C7);
      border: 1px solid var(--ap-warn, #B45309);
      border-radius: var(--ap-radius, 10px);
      margin-top: 15px;
    }

    .read-only-info {
      display: flex;
      align-items: center;
      color: var(--ap-warn, #B45309);
      font-weight: 500;
    }

    .read-only-text {
      font-size: 14px;
      line-height: 1.4;
    }

    /* Email-specific styles */
    /* Email compose card */
    .email-compose {
      margin-top: auto;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius, 10px);
      background: var(--ap-surface, #fff);
      overflow: hidden;
    }

    .email-compose-field {
      display: flex;
      align-items: flex-start;
      padding: 0 14px;
    }

    .email-compose-subject {
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
      align-items: center;
    }

    .email-compose-label {
      font-size: 13px;
      font-weight: 600;
      color: var(--ap-text-muted, #6B7280);
      min-width: 52px;
      padding: 10px 0;
      flex-shrink: 0;
    }

    .email-compose-subject-input {
      flex: 1;
      border: none;
      outline: none;
      font-family: inherit;
      font-size: 14px;
      padding: 10px 8px;
      background: transparent;
      color: var(--ap-text, #0B1220);
    }

    .email-compose-subject-hint {
      font-size: 11px;
      color: var(--ap-text-muted, #9CA3AF);
      padding: 4px 14px 8px;
      line-height: 1.4;
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
    }

    .email-compose-body {
      flex-direction: column;
      padding-top: 4px;
    }

    .email-compose-body .email-compose-label {
      padding: 2px 0;
    }

    .email-compose-textarea {
      width: 100%;
      min-height: 60px;
      max-height: 120px;
      border: none;
      outline: none;
      font-family: inherit;
      font-size: 14px;
      resize: vertical;
      padding: 6px 8px;
      margin-bottom: 6px;
      background: transparent;
      color: var(--ap-text, #0B1220);
      line-height: 1.5;
    }

    .email-compose-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 14px;
      border-top: 1px solid var(--ap-border, #E3E6EB);
      background: var(--ap-surface-muted, #F9FAFB);
    }

    .email-compose-template-btn {
      background: transparent;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      padding: 6px 14px;
      font-size: 13px;
      font-family: inherit;
      color: var(--ap-text-muted, #6B7280);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .email-compose-template-btn:hover {
      background: var(--ap-surface, #fff);
      color: var(--ap-primary, #2F5BDA);
      border-color: var(--ap-primary, #2F5BDA);
    }

    .email-compose-send-btn {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      border-radius: var(--ap-radius-sm, 6px);
      padding: 8px 22px;
      font-size: 14px;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
    }

    .email-compose-send-btn:hover:not(:disabled) {
      background: var(--ap-primary-ink, #1E3FA8);
    }

    .email-compose-send-btn:disabled {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-muted, #6B7280);
      cursor: not-allowed;
    }


.via-email-badge {
      font-size: 10px;
      font-weight: 600;
      padding: 1px 6px;
      border-radius: 4px;
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      margin-left: 4px;
      white-space: nowrap;
    }

    .per-message-subject {
      font-size: 11px;
      font-weight: 600;
      color: var(--ap-text-muted, #6B7280);
      margin-bottom: 4px;
      padding-bottom: 3px;
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .subject-change-divider {
      display: flex;
      align-items: center;
      margin: 10px 0;
    }

    .subject-divider-line {
      flex: 1;
      height: 1px;
      background-color: var(--ap-border, #E3E6EB);
    }

    .subject-divider-text {
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
      padding: 0 8px;
      white-space: nowrap;
    }

    /* New messages divider */
    .new-messages-divider {
      display: flex;
      align-items: center;
      margin: 16px 4px;
    }

    .divider-line {
      flex: 1;
      height: 1px;
      background-color: var(--ap-primary, #2F5BDA);
    }

    .divider-badge {
      background-color: var(--ap-primary, #2F5BDA);
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.8px;
      padding: 2px 10px;
      border-radius: 10px;
      margin-left: 8px;
      white-space: nowrap;
      flex-shrink: 0;
    }
  `]
})
export class MessagesComponent implements OnInit, OnDestroy, OnChanges, AfterViewChecked {
  @Input() conversationId!: string;
  @Input() contact!: Contact;
  @Input() unreadCount: number = 0;
  @Input() channelType: string = '';

  @ViewChild('messagesList') private messagesList!: ElementRef;
  @ViewChild('messageTextarea') private messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('newMessagesDivider') private newMessagesDividerRef!: ElementRef;

  public messages: Message[] = [];
  public newMessage: string = '';
  public loading: boolean = true;
  public showTemplateModal: boolean = false;

  // Email-specific properties
  public isEmailChannel: boolean = false;
  public emailSubject: string = '';
  public emailSubjectInput: string = '';

  // Index of the first unread INBOUND message. Controls the "NEW" divider.
  // -1 / >= messages.length  → no divider rendered.
  // Divider is sticky: it only disappears when the user scrolls to the bottom.
  public newMessagesStartIndex: number = -1;
  private isInitialLoad: boolean = true;
  // Captured once in ngOnInit so a concurrent markConversationAsRead (which sets
  // unreadCount → 0) does not race with the first HTTP response.
  private capturedUnreadCount: number = 0;
  private shouldScrollToDivider: boolean = false;

  private subscriptions = new Subscription();
  private shouldScrollToBottom: boolean = true;
  
  constructor(
    private conversationService: ConversationService,
    private authService: AuthService,
    private rcOversightService: RCOversightService,
    private http: HttpClient
  ) {}
  
  ngOnInit(): void {
    // Capture unreadCount NOW before markConversationAsRead races and zeros it.
    this.capturedUnreadCount = this.unreadCount;
    this.detectEmailChannel();
    this.loadMessages();
    this.initializeMessageSubscription();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    // Reload messages when conversationId changes
    if (changes['conversationId'] && !changes['conversationId'].firstChange) {
      console.log('[MessagesComponent] Conversation ID changed, reloading messages:', {
        previous: changes['conversationId'].previousValue,
        current: changes['conversationId'].currentValue
      });
      // Reset divider state for the new conversation
      this.newMessagesStartIndex = -1;
      this.isInitialLoad = true;
      this.shouldScrollToDivider = false;
      this.capturedUnreadCount = this.unreadCount;
      this.detectEmailChannel();
      this.loadMessages();
    }
    if (changes['channelType']) {
      this.detectEmailChannel();
    }
  }
  
  private initializeMessageSubscription(): void {
    // Subscribe to new message events from the conversation service
    this.subscriptions.add(
      this.conversationService.newMessage$.subscribe(event => {
        console.log('New message event received:', event);
        
        // Check if this event is for the current conversation
        if (this.conversationId === event.conversationId) {
          console.log('New message is for current conversation');
          
          if (event.action === 'created') {
            // For temporary messages (client-side created)
            if (event.messageId.startsWith('temp-')) {
              // Create a temporary message to display immediately
              const currentUser = this.authService.currentUserValue;
              const tempMessage: Message = {
                id: event.messageId,
                conversationId: this.conversationId,
                text: event.text || '', // Use the text from the event
                messageType: 'Text',
                datetime: new Date(),
                sender: {
                  type: 'User',
                  id: currentUser?.sub || '',
                  name: currentUser?.name || 'You'
                }
              };
              
              // Add to messages array if not already there
              if (!this.messages.some(m => m.id === tempMessage.id)) {
                console.log('Adding temporary message to UI:', tempMessage);
                this.messages.push(tempMessage);
                // Outbound message: advance boundary only if it isn't already pointing
                // at an unread inbound message (avoids hiding a valid contact-message divider).
                this.advanceBoundaryForOutbound();
                this.shouldScrollToBottom = true;
              }
            } else {
              // For server-generated messages, fetch the full message
              console.log('Fetching new message from server:', event.messageId);
              this.fetchNewMessage(event.messageId);
            }
          }
        }
      })
    );
  }
  
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Check if this is a read-only MC conversation for RC users
  public isReadOnlyConversation(): boolean {
    const currentUser = this.authService.currentUserValue;
    const isRCUser = currentUser?.role === UserRole.OWNER && currentUser?.company_type === 'RC';
    const activeConversation = this.conversationService.getActiveConversation();
    return isRCUser && activeConversation?.isReadOnly === true;
  }

  // Check if current user is RC super admin
  private isSuperAdmin(): boolean {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.role === UserRole.OWNER && 
           currentUser?.company_type === 'RC';
  }
  
  /**
   * Fetch a new message by ID and add it to the messages list
   */
  private fetchNewMessage(messageId: string): void {
    // Only proceed if we have a valid message ID
    if ('' === messageId) {
      console.log('%c [MessagesComponent] Invalid message ID', 'background: #e74c3c; color: white; padding: 2px 5px; border-radius: 2px;');
      return;
    }

    // If the initial load is still in progress, skip — the in-flight HTTP request will
    // return the latest messages (including this one) and call applyDividerAfterLoad with
    // isInitialLoad=true, correctly placing the "NEW" divider from capturedUnreadCount.
    // Processing this event now would kick off a second loadMessages() whose non-initial
    // applyDividerAfterLoad could clear the divider after scrollToDivider put us near bottom.
    if (this.loading) {
      console.log('%c [MessagesComponent] Skipping fetchNewMessage - initial load in progress', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;');
      return;
    }

    console.log('%c [MessagesComponent] Fetching message:', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 2px;', {
      messageId: messageId,
      conversationId: this.conversationId,
      currentMessages: this.messages.map(m => ({ id: m.id, sender: m.sender.type }))
    });

    // Check if we already have this message (avoid duplicates)
    const existingMessage = this.messages.find(m => m.id === messageId);
    if (existingMessage) {
      console.log('%c [MessagesComponent] Message already exists, updating status', 'background: #f39c12; color: white; padding: 2px 5px; border-radius: 2px;');
      existingMessage.status = 'Delivered';
      return;
    }

    // Check if this might be confirmation for a temp message we sent
    const tempMessage = this.messages.find(m => m.id.startsWith('temp-'));
    if (tempMessage) {
      console.log('%c [MessagesComponent] Found temp message, will reload to get all messages', 'background: #9b59b6; color: white; padding: 2px 5px; border-radius: 2px;', {
        tempMessageId: tempMessage.id,
        incomingMessageId: messageId
      });
    }

    // Always reload messages when we receive a websocket notification
    // This ensures messages from contacts are displayed immediately
    console.log('%c [MessagesComponent] Loading all messages', 'background: #27ae60; color: white; padding: 2px 5px; border-radius: 2px;');
    this.loadMessages();
  }
  
  ngAfterViewChecked(): void {
    if (this.shouldScrollToDivider) {
      this.scrollToDivider();
    } else if (this.shouldScrollToBottom) {
      this.scrollToBottom();
    }
  }
  
  public handleSendMessage(event?: any): void {
    // Prevent default behavior for Enter key (don't add newline)
    if (event) {
      event.preventDefault();
    }
    
    const messageText = this.newMessage.trim();
    if ('' === messageText) {
      return;
    }
    
    // Store the message text before clearing the input
    const sentMessageText = messageText;
    
    // Clear the input field immediately for better UX
    this.newMessage = '';
    
    // For email channels, pass the subject
    const emailSubject = this.isEmailChannel ? (this.emailSubjectInput || this.emailSubject || undefined) : undefined;

    this.subscriptions.add(
      this.conversationService.sendMessage(this.conversationId, sentMessageText, emailSubject).subscribe({
        next: (newMessage) => {
          console.log('Received message from service:', newMessage);
          
          // Check if the message already exists in the list (by ID or content)
          const existingIndex = this.messages.findIndex(m => 
            m.id === newMessage.id || 
            (m.id.startsWith('temp-') && m.text === newMessage.text)
          );
          
          if (-1 !== existingIndex) {
            // Replace the existing message with the updated one
            this.messages[existingIndex] = newMessage;
          } else {
            // Add the new message to the list
            this.messages.push(newMessage);
          }

          // Update conversation subject banner if user sent with a different subject
          if (this.isEmailChannel && emailSubject && !this.isNormalizedSubjectMatch(emailSubject, this.emailSubject)) {
            // Strip Re:/Fwd: prefixes — metadata always stores the base subject
            const cleanSubject = emailSubject.replace(/^(Re:\s*|Fwd:\s*|Fw:\s*)+/i, '').trim();
            this.emailSubject = cleanSubject;
            this.emailSubjectInput = 'Re: ' + cleanSubject;
            // Persist to server
            this.conversationService.getConversationById(this.conversationId).subscribe({
              next: (conv) => {
                let meta: any = {};
                if (conv?.metadata) {
                  try { meta = JSON.parse(conv.metadata); } catch { meta = {}; }
                }
                meta.subject = cleanSubject;
                this.conversationService.updateConversationMetadata(this.conversationId, JSON.stringify(meta)).subscribe();
              }
            });
          }

          // Outbound message confirmed: advance boundary if not pointing at unread inbound
          this.advanceBoundaryForOutbound();

          // Trigger scroll to bottom
          this.shouldScrollToBottom = true;
        },
        error: (error) => {
          console.error('Error sending message:', error);
        }
      })
    );
  }
  
  public isSentByUser(message: Message): boolean {
    return 'User' === message.sender.type;
  }
  
  public getSenderName(message: Message): string {
    if (true === this.isSentByUser(message)) {
      return message.sender.name || 'You';
    } else {
      return this.contact.name || 'Contact';
    }
  }
  
  private loadMessages(): void {
    this.loading = true;
    
    // Check if conversationId is defined before making the API call
    if (!this.conversationId) {
      console.error('Cannot load messages: conversationId is undefined');
      this.loading = false;
      this.messages = [];
      return;
    }

    // Check if this is a read-only MC conversation
    const activeConversation = this.conversationService.getActiveConversation();
    const isReadOnly = activeConversation?.isReadOnly === true;

    if (isReadOnly && this.isSuperAdmin()) {
      // Load messages from MC conversation using RC oversight service
      this.subscriptions.add(
        this.rcOversightService.getMCConversationMessages(this.conversationId).subscribe({
          next: (response) => {
            if (response && response.success) {
              this.messages = (response.data || []).map((message: any) => ({
                id: message.id,
                conversationId: message.conversation_id,
                text: message.content || message.text,
                messageType: message.message_type || 'Text',
                datetime: new Date(message.created_at || message.datetime),
                sender: {
                  type: message.sender_type || 'User',
                  id: message.sender_id,
                  name: message.sender_name
                }
              }));
            } else {
              this.messages = [];
            }
            this.loading = false;
            this.applyDividerAfterLoad();
          },
          error: (error) => {
            console.error('Error loading MC conversation messages:', error);
            this.loading = false;
            this.messages = [];
          }
        })
      );
    } else {
      // Load regular messages
      this.subscriptions.add(
        this.conversationService.getMessages(this.conversationId).subscribe({
          next: (messages) => {
            this.messages = messages;
            this.loading = false;
            this.applyDividerAfterLoad();
          },
          error: (error) => {
            console.error('Error loading messages:', error);
            this.loading = false;
            this.messages = [];
          }
        })
      );
    }
  }
  
  /**
   * Advance newMessagesStartIndex past the latest outbound messages, but ONLY when the
   * boundary is not already pointing at an unread inbound (Contact) message.
   * This prevents the "NEW" divider from appearing before the user's own sent messages
   * while still preserving a correctly-placed divider that marks unread contact messages.
   */
  private advanceBoundaryForOutbound(): void {
    const boundaryMsg = this.messages[this.newMessagesStartIndex];
    // If boundary is past the end OR pointing at an outbound message, advance it.
    if (!boundaryMsg || this.isSentByUser(boundaryMsg)) {
      this.newMessagesStartIndex = this.messages.length;
    }
  }

  /**
   * Called after every loadMessages() success.
   *
   * Rules:
   * 1. Initial load → no divider (all messages already seen), scroll to bottom.
   * 2. Subsequent load triggered by outbound (User) message → no divider, scroll to bottom.
   * 3. Subsequent load triggered by inbound (Contact) message AND user already at bottom
   *    → no divider (they are actively watching), scroll to bottom.
   * 4. Subsequent load triggered by inbound (Contact) message AND user is scrolled up
   *    → place/keep divider at the first new inbound message, NO scroll (stays in place).
   * 5. Divider is sticky: only cleared when user scrolls to the very bottom (onMessagesScroll).
   * 6. Only one divider: newMessagesStartIndex is always a single index.
   */
  private applyDividerAfterLoad(): void {
    if (this.isInitialLoad) {
      this.isInitialLoad = false;
      if (this.capturedUnreadCount > 0 && this.messages.length > 0) {
        // Start boundary: last N messages are unread (N = capturedUnreadCount).
        const boundaryIdx = Math.max(0, this.messages.length - this.capturedUnreadCount);
        // Find the first INBOUND (Contact) message at or after that boundary.
        // This skips any outbound messages that may sit at the boundary position.
        const firstInboundIdx = this.messages.findIndex(
          (msg, idx) => idx >= boundaryIdx && !this.isSentByUser(msg)
        );
        if (firstInboundIdx !== -1) {
          this.newMessagesStartIndex = firstInboundIdx;
          this.shouldScrollToDivider = true;
          this.shouldScrollToBottom = false;
          return;
        }
      }
      // No unread inbound messages — scroll to bottom, no divider.
      this.newMessagesStartIndex = this.messages.length;
      this.shouldScrollToBottom = true;
      this.shouldScrollToDivider = false;
      return;
    }

    // Find the first inbound (Contact) message at or after the current boundary.
    const firstNewInboundIdx = this.messages.findIndex(
      (msg, idx) => idx >= this.newMessagesStartIndex && !this.isSentByUser(msg)
    );

    if (firstNewInboundIdx === -1) {
      // Rule 2: only outbound messages since last boundary.
      // Advance boundary, but only scroll to bottom if the user is already there.
      // If they are scrolled up reading, do NOT interrupt them — they will see the
      // outbound message when they scroll down naturally.
      // (Manual sends are still scrolled by handleSendMessage's own subscriber.)
      this.newMessagesStartIndex = this.messages.length;
      this.shouldScrollToBottom = this.isUserAtBottom();
      return;
    }

    // There are new inbound messages.
    if (this.isUserAtBottom()) {
      // Rule 3: user is already at the bottom and actively watching — skip divider.
      this.newMessagesStartIndex = this.messages.length;
      this.shouldScrollToBottom = true;
      return;
    }

    // Rule 4 & 6: user scrolled up — show divider at first new inbound, no scroll.
    this.newMessagesStartIndex = firstNewInboundIdx;
    this.shouldScrollToBottom = false;
  }

  /**
   * Fired on every scroll of the messages list.
   * Clears the divider once the user reaches the bottom (content fully consumed).
   */
  public onMessagesScroll(): void {
    if (this.isUserAtBottom()) {
      this.newMessagesStartIndex = this.messages.length; // past the end → no divider
    }
  }

  /** Returns true when the messages list is scrolled to (or very near) the bottom. */
  private isUserAtBottom(): boolean {
    if (!this.messagesList) return false;
    const el = this.messagesList.nativeElement;
    return (el.scrollHeight - el.scrollTop - el.clientHeight) < 60;
  }

  private scrollToDivider(): void {
    try {
      const dividerEl = this.newMessagesDividerRef?.nativeElement;
      if (dividerEl) {
        dividerEl.scrollIntoView({ block: 'start' });
      } else {
        this.scrollToBottom();
      }
      this.shouldScrollToDivider = false;
    } catch (err) {
      this.shouldScrollToDivider = false;
    }
  }

  private scrollToBottom(): void {
    try {
      this.messagesList.nativeElement.scrollTop = this.messagesList.nativeElement.scrollHeight;
      this.shouldScrollToBottom = false;
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }
  
  public openTemplateModal(): void {
    this.showTemplateModal = true;
  }
  
  public closeTemplateModal(): void {
    this.showTemplateModal = false;
  }
  
  public onTemplateSelected(template: CommunicationTemplate): void {
    // Replace variables with available data
    let processedBody = this.replaceTemplateVariables(template.body);
    
    // Insert template content into message input
    const textarea = this.messageTextarea?.nativeElement;
    if (!textarea) {
      this.newMessage = processedBody;
    } else {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = this.newMessage;
      
      // Insert template at cursor position
      this.newMessage = currentValue.substring(0, start) + 
                       processedBody + 
                       currentValue.substring(end);
      
      // Set cursor position after the inserted template
      setTimeout(() => {
        const newPosition = start + processedBody.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      });
    }
    
    // If it's an email template with a subject, fill the subject input
    if (this.isEmailChannel && template.subject) {
      this.emailSubjectInput = this.replaceTemplateVariables(template.subject);
    }

    this.closeTemplateModal();
  }
  
  /**
   * Detect if the current conversation uses an email channel.
   * Sets isEmailChannel and loads the email subject from conversation metadata.
   */
  private detectEmailChannel(): void {
    this.isEmailChannel = this.channelType?.toLowerCase() === 'email';
    this.emailSubject = '';
    this.emailSubjectInput = '';

    if (this.isEmailChannel && this.conversationId) {
      // Load the email subject from conversation metadata
      this.subscriptions.add(
        this.conversationService.getConversationById(this.conversationId).subscribe({
          next: (conv) => {
            if (conv?.metadata) {
              try {
                const metadata = JSON.parse(conv.metadata);
                this.emailSubject = metadata.subject || '';
                if (this.emailSubject) {
                  this.emailSubjectInput = 'Re: ' + this.emailSubject;
                }
              } catch {
                // ignore parse errors
              }
            }
          },
          error: () => {
            // Non-critical, subject will just be empty
          }
        })
      );
    }
  }

  /**
   * Normalize an email subject by stripping Re:/Fwd: prefixes for comparison.
   */
  private normalizeSubject(subject: string): string {
    return subject.replace(/^(Re:\s*|Fwd:\s*|Fw:\s*)+/i, '').trim().toLowerCase();
  }

  /**
   * Check if two subjects match after normalization (stripping Re:/Fwd: prefixes).
   */
  public isNormalizedSubjectMatch(subject1: string, subject2: string): boolean {
    if (!subject1 || !subject2) return false;
    return this.normalizeSubject(subject1) === this.normalizeSubject(subject2);
  }

  /**
   * Whether to show a subject-change divider before message at given index.
   */
  public shouldShowSubjectDivider(index: number): boolean {
    if (!this.isEmailChannel || index === 0) return false;
    const current = this.messages[index].emailSubject;
    const previous = this.messages[index - 1].emailSubject;
    if (!current || !previous) return false;
    return this.normalizeSubject(current) !== this.normalizeSubject(previous);
  }

  private replaceTemplateVariables(templateBody: string): string {
    console.log('🔧 Original template body:', templateBody);
    console.log('👤 Contact data:', this.contact);
    
    let processedBody = templateBody;
    
    // Replace variables with available contact/conversation data
    const now = new Date();
    const replacements: { [key: string]: string } = {
      'candidate_name': this.contact?.name || '[Candidate Name]',
      'contact_name': this.contact?.name || '[Contact Name]',
      'phone_number': this.contact?.mobile || '[Phone Number]',
      'email': (this.contact as any)?.email || '[Email]',
      'appointment_date': now.toLocaleDateString(),
      'appointment_time': now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      'service_type': '[Service Type]',
      'location': '[Location]',
      'duration': '[Duration]',
      'role': (this.contact as any)?.job_title || '[Role]'
    };
    
    console.log('Replacements:', replacements);
    
    // Replace each variable
    Object.keys(replacements).forEach(key => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      const beforeReplace = processedBody;
      processedBody = processedBody.replace(regex, replacements[key]);
      if (beforeReplace !== processedBody) {
        console.log(`Replaced {{${key}}} with '${replacements[key]}'`);
      }
    });
    
    console.log('Final processed body:', processedBody);
    return processedBody;
  }
}
